'use client'

import Image from 'next/image'
import { useLocale } from 'next-intl'
import { Suspense } from 'react'
import { T } from '@/components/t'
import { FooterItem } from '@/features/footer/footer-item'
import { FooterLanguageSection } from '@/features/footer/footer-language-section'
import { FooterSection } from '@/features/footer/footer-section'
import { Locale } from '@/setup/locales'
import { getLinkHref, usePathname } from '@/setup/routing'
import { ExternalDeploymentUrl } from '@/setup/urls/external-deployment-urls'

export const Footer = () => {
    const locale = useLocale()
    const pathname = usePathname()

    const isDutch = locale === Locale.NL_NL || locale === Locale.EN_NL

    if (pathname.startsWith('/sumup')) {
        return null
    }

    return (
        <footer className="w-full flex flex-col items-center bg-bg-neutral-surface pt-4xl">
            <div className="w-full max-w-page px-l md:px-2xl flex flex-col gap-y-3xl">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-2xl gap-y-3xl">
                    <Suspense>
                        <FooterLanguageSection />
                    </Suspense>

                    <FooterSection title={<T t="footer.about" />}>
                        <FooterItem
                            label={<T t="footer.for-companies" />}
                            href={getLinkHref(
                                {
                                    pattern: '/for-companies',
                                    params: undefined,
                                },
                                locale as Locale
                            )}
                        />

                        <FooterItem
                            label={<T t="footer.contact" />}
                            href={getLinkHref(
                                { pattern: '/contact', params: undefined },
                                locale as Locale
                            )}
                        />

                        <FooterItem
                            label={<T t="footer.jobs" />}
                            href={getLinkHref(
                                { pattern: '/jobs', params: undefined },
                                locale as Locale
                            )}
                        />

                        <FooterItem
                            label={<T t="footer.faq" />}
                            href={getLinkHref(
                                { pattern: '/faq', params: undefined },
                                locale as Locale
                            )}
                        />

                        <FooterItem
                            label={<T t="footer.becomeAPartner" />}
                            href={getLinkHref(
                                { pattern: '/become-a-partner', params: undefined },
                                locale as Locale
                            )}
                        />

                        <FooterItem
                            label={<T t="footer.partnerSupport" />}
                            href={ExternalDeploymentUrl.PARTNER_SUPPORT[locale as Locale]}
                        />

                        {locale === Locale.EN_GB && (
                            <FooterItem
                                label="Student Discount"
                                href={'https://neotaste.com/gb/special-offer'}
                            />
                        )}

                        <FooterItem
                            label={<T t="footer.experiences" />}
                            href={getLinkHref(
                                { pattern: '/experiences', params: undefined },
                                locale as Locale
                            )}
                        />

                        {!isDutch && (
                            <FooterItem
                                label={<T t="footer.shop" />}
                                href={ExternalDeploymentUrl.SHOP[locale as Locale]}
                            />
                        )}
                    </FooterSection>

                    <FooterSection title={<T t="footer.legal" />}>
                        <FooterItem
                            label={<T t="footer.imprint" />}
                            href={getLinkHref(
                                { pattern: '/imprint', params: undefined },
                                locale as Locale
                            )}
                        />

                        <FooterItem
                            label={<T t="footer.privacyPolicy" />}
                            href={getLinkHref(
                                { pattern: '/privacy-policy', params: undefined },
                                locale as Locale
                            )}
                        />

                        <FooterItem label={<T t="footer.cookies" />} href="/#" isCookieTrigger />

                        <FooterItem
                            label={<T t="footer.termsAndConditions" />}
                            href={getLinkHref(
                                { pattern: '/terms-and-conditions', params: undefined },
                                locale as Locale
                            )}
                        />
                    </FooterSection>

                    <FooterSection title={<T t="footer.social" />}>
                        <FooterItem label="Instagram" href="https://instagram.com/neotaste.app" />

                        <FooterItem label="TikTok" href="https://www.tiktok.com/@neotaste.app" />

                        <FooterItem label="Facebook" href="https://facebook.com/neotaste.app" />

                        <FooterItem
                            label="LinkedIn"
                            href="https://www.linkedin.com/company/neotaste"
                        />
                    </FooterSection>
                </div>
            </div>

            {/* Oversized wordmark watermark, aligned to the page container like the columns
                above. The inner wrapper is deliberately shorter than the artwork's 347/54
                ratio, so the letterforms are clipped by the footer's bottom edge instead of
                sitting on it. The crop is gentler on mobile, where the wordmark is small
                enough that a deep cut costs legibility. Decorative only. */}
            <div
                aria-hidden
                className="w-full max-w-page px-l md:px-2xl mt-3xl select-none pointer-events-none"
            >
                <div className="w-full overflow-hidden aspect-[347/46] md:aspect-[347/38]">
                    <Image
                        src="/_images/navigation/neotaste-footer-logo.svg"
                        alt=""
                        height={54}
                        width={347}
                        className="w-full h-auto opacity-10"
                    />
                </div>
            </div>
        </footer>
    )
}
